'use client';

import { useState } from 'react';
import { testimonialService, type ReviewSubmission } from '@/lib/services/testimonialService';

const CATEGORY_OPTIONS = [
  { id: 'cpu', label: 'CPU 處理器' },
  { id: 'gpu', label: 'GPU 顯示卡' },
  { id: 'ram', label: 'RAM 記憶體' },
  { id: 'motherboard', label: '主機板 Motherboard' },
  { id: 'storage', label: '儲存裝置 Storage' },
  { id: 'cooling', label: '散熱器 Cooling' },
  { id: 'psu', label: '電源供應器 PSU' },
];

function StarRatingInput({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="focus:outline-none transition-transform hover:scale-110"
          aria-label={`${star} stars`}
        >
          <svg
            className={`w-8 h-8 transition-colors ${
              star <= (hovered || value) ? 'text-yellow-400' : 'text-gray-300'
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </button>
      ))}
      <span className="ml-2 text-sm text-text-muted">
        {value > 0 ? `${value}/5` : '請選擇評分'}
      </span>
    </div>
  );
}

const INITIAL_FORM: ReviewSubmission = {
  clientName: '',
  companyName: '',
  companyType: '',
  location: '',
  email: '',
  rating: 0,
  content: '',
  productCategories: [],
};

export default function ReviewSubmissionForm() {
  const [form, setForm] = useState<ReviewSubmission>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleChange(field: keyof ReviewSubmission, value: string | number | string[]) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError(null);
  }

  function toggleCategory(id: string) {
    setForm((prev) => ({
      ...prev,
      productCategories: prev.productCategories.includes(id)
        ? prev.productCategories.filter((c) => c !== id)
        : [...prev.productCategories, id],
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (form.rating === 0) {
      setError('請選擇評分 Please select a rating');
      return;
    }
    if (form.content.trim().length < 20) {
      setError('評價內容至少需要20個字 Review must be at least 20 characters');
      return;
    }
    setSubmitting(true);
    setError(null);
    const result = await testimonialService.submitReview(form);
    setSubmitting(false);
    if (result.success) {
      setSubmitted(true);
      setForm(INITIAL_FORM);
    } else {
      setError('提交失敗，請稍後再試 Submission failed, please try again');
    }
  }

  if (submitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-2xl p-10 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-heading font-black text-2xl text-navy mb-2">感謝您的評價！</h3>
        <p className="text-text-muted mb-1">Thank you for your review!</p>
        <p className="text-sm text-text-muted mt-3">
          您的評價將在審核後顯示於網站。<br />
          Your review will appear on the site after moderation.
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className="mt-6 px-6 py-2 bg-orange text-white rounded-full text-sm font-semibold hover:bg-orange/90 transition-colors"
        >
          再次評價 Submit Another
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-8 md:p-10">
      <div className="mb-8">
        <h3 className="font-heading font-black text-2xl text-navy mb-2">
          分享您的採購體驗
          <span className="text-orange"> Share Your Experience</span>
        </h3>
        <p className="text-text-muted text-sm">
          您的評價將在審核後公開顯示 · Reviews are published after moderation
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name & Company */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">
              姓名 Name <span className="text-orange">*</span>
            </label>
            <input
              type="text"
              required
              value={form.clientName}
              onChange={(e) => handleChange('clientName', e.target.value)}
              placeholder="e.g. Michael Chen"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">
              公司名稱 Company <span className="text-orange">*</span>
            </label>
            <input
              type="text"
              required
              value={form.companyName}
              onChange={(e) => handleChange('companyName', e.target.value)}
              placeholder="e.g. TechBuild Solutions Ltd"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange transition-colors"
            />
          </div>
        </div>

        {/* Company Type & Location */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">
              公司類型 Company Type
            </label>
            <select
              value={form.companyType}
              onChange={(e) => handleChange('companyType', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange transition-colors bg-white"
            >
              <option value="">請選擇 Select...</option>
              <option value="System Integrator">系統整合商 System Integrator</option>
              <option value="Electronics Retailer">電子零售商 Electronics Retailer</option>
              <option value="Computer Parts Distributor">零件分銷商 Distributor</option>
              <option value="Data Center Operator">數據中心 Data Center</option>
              <option value="Gaming Cafe Chain">電競咖啡廳 Gaming Cafe</option>
              <option value="PC Builder">電腦組裝商 PC Builder</option>
              <option value="Corporate IT">企業IT Corporate IT</option>
              <option value="Other">其他 Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">
              所在地區 Location
            </label>
            <input
              type="text"
              value={form.location}
              onChange={(e) => handleChange('location', e.target.value)}
              placeholder="e.g. Hong Kong, Taipei, Seoul"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange transition-colors"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-semibold text-navy mb-1.5">
            電子郵件 Email <span className="text-orange">*</span>
          </label>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="your@company.com"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange transition-colors"
          />
          <p className="text-xs text-text-muted mt-1">電郵不會公開顯示 · Email will not be displayed publicly</p>
        </div>

        {/* Rating */}
        <div>
          <label className="block text-sm font-semibold text-navy mb-2">
            整體評分 Overall Rating <span className="text-orange">*</span>
          </label>
          <StarRatingInput value={form.rating} onChange={(v) => handleChange('rating', v)} />
        </div>

        {/* Product Categories */}
        <div>
          <label className="block text-sm font-semibold text-navy mb-2">
            採購產品類別 Product Categories
          </label>
          <div className="flex flex-wrap gap-2">
            {CATEGORY_OPTIONS.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => toggleCategory(cat.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 ${
                  form.productCategories.includes(cat.id)
                    ? 'bg-orange text-white border-orange shadow-sm'
                    : 'bg-white text-navy border-gray-200 hover:border-orange/50'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Review Content */}
        <div>
          <label className="block text-sm font-semibold text-navy mb-1.5">
            評價內容 Review Content <span className="text-orange">*</span>
          </label>
          <textarea
            required
            rows={5}
            value={form.content}
            onChange={(e) => handleChange('content', e.target.value)}
            placeholder="請分享您與PC CARGO的採購體驗... Please share your experience with PC CARGO..."
            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange transition-colors resize-none"
          />
          <p className="text-xs text-text-muted mt-1">
            {form.content.length}/500 · 最少20字 Minimum 20 characters
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3 bg-orange text-white font-bold rounded-xl hover:bg-orange/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              提交中... Submitting...
            </>
          ) : (
            '提交評價 Submit Review'
          )}
        </button>
      </form>
    </div>
  );
}
